import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import { MICHELIN_GET_PACKAGES } from '@web-platform/shared-universal-types/src/network/requestTypes';
import { formatCurrency } from '@web-platform/shared-universal-utils-frontend-localisation';

import { USER_REQUESTED_SEARCH } from '../../../../actions/searchControls';

import type { SkyscannerMichelinV1Packages } from '@skyscanner-internal/michelin-web-http-client';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type {
  PackagesState,
  PackagesFlightDetails,
  PackageUpsellCardProps,
} from '@web-platform/shared-universal-types/src/redux/state/shared/michelin/packages';

export const INITIAL_STATE: PackagesState = {
  flight: {
    outbound: null,
    inbound: null,
  },
  packages: [],
  packagesSearchUri: '',
  itineraryMatch: false,
  hasLoaded: false,
};

const transformFlightLeg = (
  leg: SkyscannerMichelinV1Packages.PackagesFlightLegDetails | undefined,
): PackagesFlightDetails | undefined => {
  if (!leg?.segments || leg.segments.length === 0) return undefined;

  const firstSegment = leg.segments[0];
  const lastSegment = leg.segments[leg.segments.length - 1];

  return {
    originAirportCode: firstSegment.airport?.code || '',
    originAirportName: firstSegment.airport?.name || '',
    destinationAirportCode: lastSegment.airport?.code || '',
    destinationAirportName: lastSegment.airport?.name || '',
    departureLocalTime: firstSegment.localTime || '',
    arrivalLocalTime: lastSegment.localTime || '',
    airlineName: firstSegment.airline?.name || '',
    airlineLogo: firstSegment.airline?.logoUri || '',
    stops: leg.stops || 0,
    direct: leg.direct || false,
    duration: leg.duration || '',
  };
};

const transformHotelToCard = (
  hotel: SkyscannerMichelinV1Packages.PackagesHotel,
  nightsStay: number,
): PackageUpsellCardProps => {
  const price = formatCurrency(Number(hotel.price?.amount));

  return {
    hotelId: hotel.id || '',
    hotelName: hotel.name || '',
    hotelImage: hotel.imageUri || '',
    providerName: hotel.partnerName || '',
    providerLogo: hotel.partnerLogo || '',
    price,
    redirectUrl: hotel.packageUri || '',
    neighborhood: hotel.neighbourhood || hotel.city,
    starRating: hotel.stars,
    rating: hotel.rating
      ? {
          score: hotel.rating.score || 0,
          count: hotel.rating.count || 0,
        }
      : undefined,
    nightsStay,
  };
};

const transformPackagesData = (
  response: SkyscannerMichelinV1Packages.PackagesResponse,
): Omit<PackagesState, 'hasLoaded'> => {
  if (!response) return INITIAL_STATE;

  const outbound = transformFlightLeg(response.flightDetails?.outbound);
  const inbound = transformFlightLeg(response.flightDetails?.inbound);
  const hotels = response.hotels || [];
  const nightsStay = response.nightsStay || 1;

  if (!outbound || !inbound || hotels.length === 0) return INITIAL_STATE;

  const transformedHotels: PackageUpsellCardProps[] = hotels.map((hotel) =>
    transformHotelToCard(hotel, nightsStay),
  );

  return {
    flight: {
      outbound,
      inbound,
    },
    packages: transformedHotels,
    packagesSearchUri: response.packagesSearchUri || '',
    itineraryMatch: response.flightDetails?.itineraryMatch || false,
  };
};

const packagesReducer = (state: PackagesState = INITIAL_STATE, action: Action): PackagesState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      const { payload } = action;

      if (payload.type === MICHELIN_GET_PACKAGES) {
        if (
          payload.response?.error ||
          !payload.response?.body ||
          typeof payload.response.body !== 'object'
        ) {
          return { ...INITIAL_STATE, hasLoaded: true };
        }

        return { ...transformPackagesData(payload.response.body), hasLoaded: true };
      }

      return state;
    }

    case USER_REQUESTED_SEARCH: {
      const { payload } = action;
      if (payload.searched) {
        return INITIAL_STATE;
      }
      return state;
    }

    case SEARCH_PARAMS_CHANGED:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default packagesReducer;

export const get = (state: PackagesState): PackagesState => state;
